/*
 Settings for prototype features, if they are merged into the main app
 these settings should be moved to localConfig

 */
import { defineStore } from "pinia";
import { getStorageItem, removeStorageItem } from "@/libs/local-storage";

export type TOverviewSlug = "overview" | "overview-b";

// Bump version number to reset settings for all users
const localStorageKey = "localSettingsPrototype-1";

export type ConfigState = {
    uiOverview: {
        showKpis: boolean;
        showAiChat: boolean;
        showMarketValueChart: boolean;
        showPnlTreeMap: boolean;
        showInsights: boolean;
        showMarket: boolean;
        showNews: boolean;
        showPersonalBrief: boolean;
        showStartActions: boolean;
        limitWidth: boolean;
        floatAi: boolean;
        selectedView: string;
        overviewPage: TOverviewSlug;
        compactAllocations: boolean;
        pnlTreeMapMarketSize: boolean;
        newMenu: boolean;
        betterChartAnimation: boolean;
        kpis: {
            totalValue: boolean;
            cash: boolean;
            loans: boolean;
            netWorth: boolean;
            perf1D: boolean;
            perf1W: boolean;
            perfYTD: boolean;
            perf1Y: boolean;
        };
    };
};

// Define the default state structure separately for clarity
const defaultState: ConfigState = {
    uiOverview: {
        showKpis: true,
        showAiChat: false,
        showMarketValueChart: true,
        showPnlTreeMap: true,
        showInsights: false,
        showMarket: true,
        showNews: true,
        showPersonalBrief: true,
        showStartActions: true,
        limitWidth: true,
        floatAi: true,
        selectedView: "cards",
        overviewPage: "overview-b",
        compactAllocations: true,
        newMenu: true,
        pnlTreeMapMarketSize: false, // Default to sizing by PnL amount
        betterChartAnimation: true,
        kpis: {
            totalValue: true,
            cash: true,
            loans: true,
            netWorth: true,
            perf1D: true,
            perf1W: true,
            perfYTD: true,
            perf1Y: true,
        },
    },
};

export const usePrototypeConfigStore = defineStore("prototypeConfig", {
    // Use 'prototypeConfig' as ID to avoid potential conflicts if 'config' is used elsewhere
    state: (): ConfigState => {
        // Initialize state from localStorage only on the client side
        if (import.meta.server) {
            return defaultState; // Return default state on server
        }

        const parsedConfig = getStorageItem(localStorageKey);

        if (parsedConfig) {
            // Perform a deep merge to handle potentially missing keys and ensure defaults are applied
            const mergedUiOverview = {
                ...defaultState.uiOverview, // Start with defaults
                ...(parsedConfig.uiOverview || {}), // Merge stored uiOverview if it exists
                kpis: {
                    ...defaultState.uiOverview.kpis, // Start with default kpis
                    ...(parsedConfig.uiOverview?.kpis || {}), // Merge stored kpis if they exist
                },
                // Explicitly merge other potentially missing fields if necessary,
                // although the spread operator should handle most cases.
                // Example:
                // showPersonalBrief: parsedConfig.uiOverview?.showPersonalBrief ?? defaultState.uiOverview.showPersonalBrief,
                // floatAi: parsedConfig.uiOverview?.floatAi ?? defaultState.uiOverview.floatAi,
                // selectedView: parsedConfig.uiOverview?.selectedView ?? defaultState.uiOverview.selectedView,
            };
            return {
                ...defaultState, // Keep other top-level defaults if any
                ...parsedConfig, // Merge other top-level stored properties
                uiOverview: mergedUiOverview, // Use the carefully merged uiOverview
            };
        } else {
            // If nothing in storage or parsing failed, return default state
            return defaultState;
        }
    },

    actions: {
        setSelectedView(view: string) {
            this.uiOverview.selectedView = view;
        },
        setOverviewPage(page: TOverviewSlug) {
            this.uiOverview.overviewPage = page;
        },
        showAiChat(show: boolean) {
            this.uiOverview.showAiChat = show;
        },
        toggleAIChat() {
            this.uiOverview.showAiChat = !this.uiOverview.showAiChat;
        },
        resetToDefaults() {
            if (typeof window !== "undefined") {
                removeStorageItem(localStorageKey);
            }
            // Deep clone defaultState and patch it into the current state
            this.$patch(JSON.parse(JSON.stringify(defaultState)));
        },
    },
});

// --- LocalStorage Synchronization ---
// This needs to be handled carefully, especially in Nuxt 3.
// Subscribing directly here might run too early or on the server.
// A common pattern is to use a plugin or watch within the component/layout where the store is used.

// Option 1: Use a Nuxt plugin (Recommended for Nuxt)
// Create a file like plugins/prototypeConfigSync.client.ts:
/*
import { usePrototypeConfigStore } from '~/store/prototypeConfig';
import { watch } from 'vue';
import { setStorageItem } from "@/libs/local-storage";

export default defineNuxtPlugin(() => {
  if (process.client) {
    const store = usePrototypeConfigStore();
    const localStorageKey = 'localSettingsPrototype'; // Ensure key matches

    // Watch the entire state for changes and save to localStorage
    watch(
      store.$state,
      (newState) => {
        setStorageItem(localStorageKey, JSON.stringify(newState));
      },
      { deep: true } // Watch nested properties
    );
  }
});
*/

// Option 2: Watch within the component/layout (Simpler for limited use)
// If you prefer not to use a plugin, you could add a watcher
// inside main.vue's setup script:
/*
import { watch } from 'vue';
import { setStorageItem } from "@/libs/local-storage";

const configStore = usePrototypeConfigStore();
if (process.client) {
  watch(
    configStore.$state,
    (newState) => {
      setStorageItem('localSettingsPrototype', JSON.stringify(newState));
    },
    { deep: true }
  );
}
*/

// --- LocalStorage Synchronization ---
// The watcher logic has been moved to plugins/prototypeConfigSync.client.ts
// to ensure Pinia is active before accessing the store.
